import { useCallback, useEffect, useRef, useState } from "react";
import api from "../lib/api";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import CompagniaDettaglioDialog from "./CompagniaDettaglioDialog";
import { SquarePen } from "lucide-react";

// Dropdown ISPETTORATO: carica gli ispettorati sinistri della compagnia selezionata
// (campo ASSICURAZIONE) dalla scheda anagrafica di quella compagnia. Si azzera al
// cambio compagnia. Se la compagnia non ha ispettorati mostra un placeholder disabilitato.
// Accanto al campo, se la compagnia esiste in archivio, un pulsante apre la scheda
// compagnia (sezione "Ispettorato Sinistri") per aggiungere/modificare gli ispettorati
// senza uscire dal flusso corrente.
export default function IspettoratoField({ label, value, onChange, compagnia, testId }) {
  const [opts, setOpts] = useState([]);
  const [rec, setRec] = useState(null);
  const [schedaOpen, setSchedaOpen] = useState(false);
  const prevComp = useRef(compagnia);
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  const optsFromRec = (r) =>
    Array.isArray(r?.ispettorati) ? r.ispettorati.map((x) => (x.denominazione || "").trim()).filter(Boolean) : [];

  const findRec = useCallback(async () => {
    const c = (compagnia || "").trim();
    if (!c) return null;
    try {
      const { data } = await api.get("/anagrafiche/compagnie");
      return (data || []).find((r) => (r.denominazione || r.nome || "").trim().toLowerCase() === c.toLowerCase()) || null;
    } catch {
      return null;
    }
  }, [compagnia]);

  useEffect(() => {
    let active = true;
    (async () => {
      const found = await findRec();
      if (!active) return;
      setRec(found);
      setOpts(optsFromRec(found));
      if (prevComp.current !== compagnia) {
        onChangeRef.current && onChangeRef.current("");
        prevComp.current = compagnia;
      }
    })();
    return () => { active = false; };
  }, [compagnia, findRec]);

  const handleSaved = (doc) => {
    if (doc) {
      setRec(doc);
      setOpts(optsFromRec(doc));
    }
  };

  return (
    <div>
      <label className="block text-sm text-slate-300 mb-1.5">{label}</label>
      <div className="flex items-center gap-2">
        <div className="flex-1 min-w-0">
          {opts.length === 0 ? (
            <div data-testid={testId} className="h-9 flex items-center px-3 rounded-md bg-slate-900/60 border border-slate-700 text-sm text-slate-500">
              Nessun ispettorato disponibile
            </div>
          ) : (
            <Select value={value || undefined} onValueChange={(v) => onChange && onChange(v === "__none__" ? "" : v)}>
              <SelectTrigger data-testid={testId} className="h-9 bg-slate-900/60 border-slate-700 text-slate-100"><SelectValue placeholder="Seleziona ispettorato" /></SelectTrigger>
              <SelectContent className="bg-[#121824] border-slate-700 text-slate-100">
                <SelectItem value="__none__" className="text-slate-400">— Nessuno —</SelectItem>
                {opts.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
              </SelectContent>
            </Select>
          )}
        </div>
        {rec && (
          <button
            type="button"
            onClick={() => setSchedaOpen(true)}
            data-testid={testId ? `${testId}-edit` : undefined}
            title={`Aggiungi / modifica ispettorati di ${rec.denominazione || rec.ragione_sociale || "compagnia"}`}
            className="h-9 w-9 shrink-0 flex items-center justify-center rounded-md border border-slate-700 bg-slate-800/60 text-slate-300 hover:text-cyan-300 hover:border-cyan-500/50 hover:bg-cyan-500/10 transition-colors"
          >
            <SquarePen className="h-4 w-4" />
          </button>
        )}
      </div>
      {rec && (
        <CompagniaDettaglioDialog
          open={schedaOpen}
          onOpenChange={(o) => !o && setSchedaOpen(false)}
          rec={rec}
          defaultTab="ispettorati"
          onSaved={handleSaved}
        />
      )}
    </div>
  );
}
